import { ChangeDetectionStrategy, Component, computed, forwardRef, input, signal } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { CadsHeadingDetailBitComponent } from '../../bits/cads-heading-detail-bit/cads-heading-detail-bit.component';
import type { CadsHeadingDetailBitConfig } from '../../bits/cads-heading-detail-bit/cads-heading-detail-bit.config';
import { nextCadsFormKitId } from '../internal/cads-form-kit-ids';

import type { CadsRadioGroupConfig, CadsRadioGroupOption } from './cads-radio-group.config';

/**
 * Reactive-forms native radio group whose value is the selected option's `id` (or `null`). Implements
 * {@link ControlValueAccessor}. The host element exposes `role="radiogroup"`; row inputs share a `name` so the browser
 * enforces single-selection and arrow-key navigation. Set `config.cardStyle` to **false** for borderless rows inside an
 * outer `cads-card-view` (see styleguide).
 */
@Component({
    selector: 'cads-radio-group',
    imports: [CadsHeadingDetailBitComponent],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CadsRadioGroupComponent),
            multi: true,
        },
    ],
    templateUrl: './cads-radio-group.component.html',
    styleUrl: './cads-radio-group.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'cads-radio-group-host',
        role: 'radiogroup',
        '[attr.aria-labelledby]': '_groupLabelId()',
        '[attr.aria-describedby]': '_showCaption() ? _captionId() : null',
        '[attr.aria-invalid]': '_hasError() ? "true" : null',
        '[attr.aria-required]': '_ariaRequired()',
    }
})
export class CadsRadioGroupComponent implements ControlValueAccessor {
  readonly config = input.required<CadsRadioGroupConfig>();

  private readonly _fallbackGroupId = nextCadsFormKitId('cads-radio-group');

  protected readonly _selectedId = signal<string | null>(null);
  protected readonly _controlDisabled = signal(false);

  protected readonly _groupId = computed(() => {
    const fromConfig = this.config().groupId?.trim();
    return fromConfig || this._fallbackGroupId;
  });

  protected readonly _groupLabelId = computed(() => (this.config().label ? `${this._groupId()}-label` : null));

  protected readonly _captionId = computed(() => `${this._groupId()}-caption`);

  protected readonly _hasError = computed(() => (this.config().errorMessage?.trim() ?? '').length > 0);

  protected readonly _ariaRequired = computed(() => (this.config().required === true ? 'true' : null));

  protected readonly _showHelper = computed(() => {
    if (this._hasError()) {
      return false;
    }
    return (this.config().helperText?.trim() ?? '').length > 0;
  });

  protected readonly _showCaption = computed(() => this._hasError() || this._showHelper());

  protected readonly _captionText = computed(() => {
    if (this._hasError()) {
      return (this.config().errorMessage ?? '').trim();
    }
    return this.config().helperText?.trim() ?? '';
  });

  protected readonly _groupDisabled = computed(() => this._controlDisabled() || this.config().disabled === true);

  private _onChange: (value: string | null) => void = () => undefined;
  private _onTouched: () => void = () => undefined;

  writeValue(value: string | null | undefined): void {
    this._selectedId.set(value ?? null);
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._controlDisabled.set(isDisabled);
  }

  protected _isSelected(option: CadsRadioGroupOption): boolean {
    return this._selectedId() === option.id;
  }

  protected _isDisabled(option: CadsRadioGroupOption): boolean {
    return this._groupDisabled() || option.disabled === true;
  }

  protected _rowInputId(option: CadsRadioGroupOption): string {
    return `${this._groupId()}-${option.id}`;
  }

  protected _onRowChange(option: CadsRadioGroupOption, event: Event): void {
    if (this._isDisabled(option)) {
      return;
    }
    const checked = (event.target as HTMLInputElement).checked;
    if (!checked) {
      return;
    }
    this._selectedId.set(option.id);
    this._onChange(option.id);
    this._onTouched();
  }

  protected _heading(option: CadsRadioGroupOption): CadsHeadingDetailBitConfig | null {
    return option.heading ?? null;
  }

  protected _subtitleOnly(option: CadsRadioGroupOption): string | null {
    if (option.heading) {
      return null;
    }
    const t = option.subtitleOnly?.trim();
    return t ? t : null;
  }

  protected _cardStyle(option: CadsRadioGroupOption): boolean {
    if (this.config().cardStyle === false) {
      return false;
    }
    return option.cardStyle !== false;
  }
}
